import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const ZALMA_URL = process.env.REACT_APP_ZALMA_URL || 'https://www.zalma.com.au';

function AboutHero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="max-w-3xl">
          <span className="inline-block text-[12px] font-bold tracking-[0.18em] uppercase text-blue-600 mb-6">About Zap AI</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: 'Manrope' }}>
            Building practical AI for<br />
            <span className="text-blue-600">everyday businesses.</span>
          </h1>
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <section className="py-24 bg-slate-50/40">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-blue-600" style={{ fontFamily: 'Manrope' }}>
              Our mission
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              We believe AI can quietly transform the working week of a small business. Service operators run on a hundred small operational details: bookings, reminders, client records, invoices, follow-ups. Most of these tasks are repetitive, time-consuming, and not why anyone got into the business.
            </p>
            <p>
              Our mission is to build AI-powered software that disappears into the background. Software that takes those repetitive tasks off the operator's plate so they can spend more time on the work they actually love and the customers they actually serve.
            </p>
            <p>
              We believe in deep specialisation over generic horizontal tools. We pick one industry at a time, learn it thoroughly, and ship a product shaped around how that industry actually works.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-blue-600" style={{ fontFamily: 'Manrope' }}>
              The Zap AI story
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              Zap AI started with a familiar problem. We kept hearing from owners of small service businesses (groomers, vets, GPs, allied health) that they were drowning in admin. Off-the-shelf software was either too generic to be useful or too complex to deploy without an IT team. Generative AI was a buzzword without a clear path to real productivity gains.
            </p>
            <p>
              We started Zap AI to close that gap. To use AI where it earns its place, in chat assistants, smart reminders, document drafting, and to leave deterministic software where determinism wins.
            </p>
            <p>
              Our first product, Zalma, is built for pet grooming salons. We chose grooming because the workflow is specific, the operators are underserved, and the pain points are real and measurable. Every feature in Zalma is shaped by feedback from actual salons.
            </p>
            <p>
              We are headquartered in Australia. Our customers are AU and NZ businesses. We are deliberately a small team focused on shipping great software, not raising rounds.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsSection() {
  return (
    <section className="py-24 bg-slate-50/40">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-blue-600">Our products</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-3" style={{ fontFamily: 'Manrope' }}>
              What we build
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200/60 bg-white p-8">
            <span className="inline-block text-[11px] font-bold tracking-[0.15em] uppercase text-amber-700 bg-amber-100 rounded-full px-3 py-1 mb-4">Available now</span>
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
              Zalma
            </h3>
            <p className="text-base text-slate-600 leading-relaxed mb-5">
              The complete operating system for pet grooming salons. Online bookings, pet and client records, staff scheduling, AI client communication, and business analytics.
            </p>
            <a href={ZALMA_URL}>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">
                Visit Zalma <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
          <div className="rounded-2xl border border-slate-200/60 bg-slate-50/80 p-8">
            <span className="inline-block text-[11px] font-bold tracking-[0.15em] uppercase text-slate-700 bg-slate-200 rounded-full px-3 py-1 mb-4">In research</span>
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
              Beyond grooming
            </h3>
            <p className="text-base text-slate-600 leading-relaxed mb-5">
              We are researching adjacent industries where the same playbook applies. Vet clinics, dental practices, allied health, and other appointment-based service businesses are on our shortlist. If your industry should be next, we would love to hear from you.
            </p>
            <Link to="/faq">
              <Button variant="outline" className="rounded-full px-6">
                Read our FAQ
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrinciplesSection() {
  const principles = [
    {
      title: 'Build something great',
      desc: 'We aim high. We don\'t settle for okay or accept mediocrity. Seeing something done poorly drives us to roll up our sleeves and raise the bar. We ship early because greatness is paved with rapid iteration and real-world feedback.',
    },
    {
      title: 'Deep specialisation',
      desc: 'We pick one industry at a time and learn it thoroughly. Generic horizontal tools never beat a product built specifically for how a given industry actually works. Depth over breadth.',
    },
    {
      title: 'Practical AI',
      desc: 'AI is a means, not an end. We use it where it saves real time. We avoid it where deterministic software is faster, cheaper, and more reliable. Buzzwords don\'t ship.',
    },
    {
      title: 'Privacy first',
      desc: 'Customer data is yours. Hosted in Australia, encrypted at rest and in transit, with strict tenant isolation by default. We do not use customer data to train general-purpose AI models.',
    },
  ];
  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-blue-600">What we believe</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-3" style={{ fontFamily: 'Manrope' }}>
              Our operating principles
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
          {principles.map((p, i) => (
            <div key={i} className="border-t border-slate-200 pt-6">
              <h3 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                {p.title}
              </h3>
              <p className="text-base text-slate-500 leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTABlock() {
  return (
    <section className="py-24 bg-blue-600">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
          Want to learn more?
        </h2>
        <p className="mt-4 text-base md:text-lg text-blue-100 max-w-xl mx-auto">
          Read our FAQ or get in touch. We respond to every email personally.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/faq">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 h-12 text-base font-semibold shadow-lg">
              Read FAQ <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <a href="mailto:info@zapai.com.au">
            <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-blue-600 rounded-full px-8 h-12 text-base font-semibold">
              Get in touch
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      <Navbar />
      <AboutHero />
      <MissionSection />
      <StorySection />
      <ProductsSection />
      <PrinciplesSection />
      <CTABlock />
      <Footer />
    </div>
  );
}
